import { Text } from '@/components/animated-type/text';
import { AnnotatedType } from '@/components/annotated-type';
import { StoryHeader, StoryTagline, StoryTheme, StoryTitle } from '@/components/microstory/header';
import { Outro } from '@/components/microstory/outro';
import { StoriesNavigation } from '@/components/microstory/stories-navigation';
import { CenteredStoryText } from '@/components/microstory/text';
import { Theme, Themes } from '@/components/microstory/theme';
import { SeoHead } from '@/components/seo-head';
import { MentalBurdensChapter1 } from '@/components/stories/mental-burdens/chapter-1';
import { MentalBurdensChapter2 } from '@/components/stories/mental-burdens/chapter-2';
import { MentalBurdensChapter3 } from '@/components/stories/mental-burdens/chapter-3';
import { MentalBurdensQuestion1 } from '@/components/stories/mental-burdens/question-1';
import { MentalBurdensQuestion2 } from '@/components/stories/mental-burdens/question-2';
import { MentalBurdensQuestion3 } from '@/components/stories/mental-burdens/question-3';
import { SurveyBackground } from '@/components/survey-background';
import data from '@/data/mental-burdens.json';
import { createGetServerSideProps } from '@/utils/create-get-server-side-props';
import { getStoryProps } from '@/utils/get-story-props';
import { useLayout } from '@/utils/use-layout';
import { useSharingImage } from '@/utils/use-sharing-image';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { InferGetServerSidePropsType } from 'next';

export const getServerSideProps = createGetServerSideProps(
  async () => ({ data: data as MentalBurdensData }),
  getStoryProps('mental-burdens', 'under-pressure')
);

export default function MentalBurdens(
  props: InferGetServerSidePropsType<typeof getServerSideProps>
) {
  const { storySlug, baseUrl, data, sharingUrl, linkedStoryIds, relatedStoryId } = props;

  useLayout('white');
  useLingui();

  const sharingImage = useSharingImage('mental-burdens');
  const pageTitle = t({ message: 'Mental burdens', id: 'mental-burdens.title' });
  const pageDescription = t({
    id: 'mental-burdens.description',
    message: 'How do people feel on the inside?',
  });

  return (
    <>
      <SeoHead
        baseUrl={baseUrl}
        pageTitle={pageTitle}
        pageDescription={pageDescription}
        sharingImage={sharingImage}
      />

      <Theme theme={Themes.lime} hasBackButton>
        <StoryHeader backgroundId={storySlug}>
          <StoryTheme>
            <Trans id="mental-burdens.theme">Mental Health</Trans>
          </StoryTheme>
          <StoryTitle>{pageTitle}</StoryTitle>
          <StoryTagline>
            <Trans id="mental-burdens.tagline">
              <Text type="heading">
                How do people feel on the <span>inside?</span>
              </Text>
            </Trans>
          </StoryTagline>
        </StoryHeader>

        <SurveyBackground />

        <CenteredStoryText>
          <Trans id="mental-burdens.text-1">
            We asked both young and older people about their{' '}
            <AnnotatedType>mental well-being.</AnnotatedType> Take the quiz to see how they
            responded.
          </Trans>
        </CenteredStoryText>

        <MentalBurdensQuestion1 />
        <MentalBurdensChapter1 data={data} />

        <MentalBurdensQuestion2 />
        <MentalBurdensChapter2 data={data} />

        <CenteredStoryText>
          <Trans id="mental-burdens.text-2">
            Mental burdens are more prevalent among some young people than others.
          </Trans>
        </CenteredStoryText>

        <MentalBurdensQuestion3 />
        <MentalBurdensChapter3 data={data} />

        <Outro
          relatedStoryId={relatedStoryId}
          sharing={{
            url: sharingUrl,
            title: t({ id: 'mental-burdens.sharing.title', message: 'Mental burdens' }),
            text: t({
              id: 'mental-burdens.sharing.text',
              message: 'How do people feel on the inside?',
            }),
          }}
        >
          <Trans id="mental-burdens.wrap-up">
            What do you think can be done to improve young people's{' '}
            <AnnotatedType>mental well-being?</AnnotatedType>
          </Trans>
        </Outro>
      </Theme>

      <StoriesNavigation storyIds={linkedStoryIds} />
    </>
  );
}
